/**
 * useCollections Hook
 *
 * API integration for user-defined collections.
 * Handles CRUD operations for both schemas and items.
 */

import { useState, useCallback, useEffect } from 'react';
import type { CollectionSchema, CollectionItem } from '@deep-agents/shared-types';

// ============================================================
// TYPES
// ============================================================

export interface CollectionSchemaRecord {
  id: string;
  name: string;
  schema: CollectionSchema;
  createdAt: number;
  updatedAt: number;
}

export interface CollectionItemRecord {
  id: string;
  collectionName: string;
  data: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

interface UseCollectionsOptions {
  appId: string;
}

interface GenerateResult {
  generated: number;
  items: CollectionItemRecord[];
}

interface GenerateSchemaResult {
  schema: CollectionSchema;
  message: string;
}

interface UseCollectionsReturn {
  // Schema operations
  schemas: CollectionSchemaRecord[];
  isLoadingSchemas: boolean;
  schemasError: string | null;
  fetchSchemas: () => Promise<void>;
  createSchema: (name: string, schema: CollectionSchema) => Promise<CollectionSchemaRecord | null>;
  updateSchema: (name: string, schema: CollectionSchema) => Promise<boolean>;
  deleteSchema: (name: string) => Promise<boolean>;

  // Item operations
  items: CollectionItemRecord[];
  itemsCount: number;
  isLoadingItems: boolean;
  itemsError: string | null;
  fetchItems: (collectionName: string, page?: number, limit?: number) => Promise<void>;
  createItem: (collectionName: string, data: Record<string, unknown>) => Promise<CollectionItemRecord | null>;
  updateItem: (collectionName: string, itemId: string, data: Record<string, unknown>) => Promise<boolean>;
  deleteItem: (collectionName: string, itemId: string) => Promise<boolean>;
  importItems: (collectionName: string, data: Record<string, unknown>[], format?: 'json' | 'csv') => Promise<{ imported: number } | null>;

  // AI generation
  isGenerating: boolean;
  generateItems: (collectionName: string, prompt: string, count?: number) => Promise<GenerateResult | null>;

  // AI schema assistant
  isGeneratingSchema: boolean;
  generateSchema: (description: string, examples?: string) => Promise<GenerateSchemaResult | null>;
}

// ============================================================
// HOOK IMPLEMENTATION
// ============================================================

export function useCollections({ appId }: UseCollectionsOptions): UseCollectionsReturn {
  // Schema state
  const [schemas, setSchemas] = useState<CollectionSchemaRecord[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false);
  const [schemasError, setSchemasError] = useState<string | null>(null);

  // Items state
  const [items, setItems] = useState<CollectionItemRecord[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  // AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingSchema, setIsGeneratingSchema] = useState(false);

  // ─────────────────────────────────────────────────────────
  // Schema Operations
  // ─────────────────────────────────────────────────────────

  const fetchSchemas = useCallback(async () => {
    if (!appId) return;

    setIsLoadingSchemas(true);
    setSchemasError(null);

    try {
      const response = await fetch(`/api/apps/${appId}/collections`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch collections');
      }

      const result = await response.json();
      if (result.success) {
        setSchemas(result.data.collections || result.data.schemas || []);
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err) {
      setSchemasError(err instanceof Error ? err.message : 'Failed to fetch collections');
    } finally {
      setIsLoadingSchemas(false);
    }
  }, [appId]);

  const createSchema = useCallback(async (
    name: string,
    schema: CollectionSchema
  ): Promise<CollectionSchemaRecord | null> => {
    if (!appId) return null;

    try {
      const response = await fetch(`/api/apps/${appId}/collections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, schema }),
      });

      const result = await response.json();
      if (result.success) {
        // Refresh the list
        await fetchSchemas();
        return result.data.schema;
      } else {
        throw new Error(result.error || 'Failed to create collection');
      }
    } catch (err) {
      setSchemasError(err instanceof Error ? err.message : 'Failed to create collection');
      return null;
    }
  }, [appId, fetchSchemas]);

  const updateSchema = useCallback(async (
    name: string,
    schema: CollectionSchema
  ): Promise<boolean> => {
    if (!appId) return false;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ schema }),
      });

      const result = await response.json();
      if (result.success) {
        await fetchSchemas();
        return true;
      } else {
        throw new Error(result.error || 'Failed to update collection');
      }
    } catch (err) {
      setSchemasError(err instanceof Error ? err.message : 'Failed to update collection');
      return false;
    }
  }, [appId, fetchSchemas]);

  const deleteSchema = useCallback(async (name: string): Promise<boolean> => {
    if (!appId) return false;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${name}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        await fetchSchemas();
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete collection');
      }
    } catch (err) {
      setSchemasError(err instanceof Error ? err.message : 'Failed to delete collection');
      return false;
    }
  }, [appId, fetchSchemas]);

  // ─────────────────────────────────────────────────────────
  // Item Operations
  // ─────────────────────────────────────────────────────────

  const fetchItems = useCallback(async (
    collectionName: string,
    page = 1,
    limit = 50
  ) => {
    if (!appId || !collectionName) return;

    setIsLoadingItems(true);
    setItemsError(null);

    try {
      const offset = (page - 1) * limit;
      const response = await fetch(
        `/api/apps/${appId}/collections/${collectionName}/items?limit=${limit}&offset=${offset}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch items');
      }

      const result = await response.json();
      if (result.success) {
        setItems(result.data.items || []);
        setItemsCount(result.data.total || 0);
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to fetch items');
    } finally {
      setIsLoadingItems(false);
    }
  }, [appId]);

  const createItem = useCallback(async (
    collectionName: string,
    data: Record<string, unknown>
  ): Promise<CollectionItemRecord | null> => {
    if (!appId) return null;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${collectionName}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ data }),
      });

      const result = await response.json();
      if (result.success) {
        await fetchItems(collectionName);
        return result.data.item;
      } else {
        throw new Error(result.error || 'Failed to create item');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to create item');
      return null;
    }
  }, [appId, fetchItems]);

  const updateItem = useCallback(async (
    collectionName: string,
    itemId: string,
    data: Record<string, unknown>
  ): Promise<boolean> => {
    if (!appId) return false;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${collectionName}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ data }),
      });

      const result = await response.json();
      if (result.success) {
        await fetchItems(collectionName);
        return true;
      } else {
        throw new Error(result.error || 'Failed to update item');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to update item');
      return false;
    }
  }, [appId, fetchItems]);

  const deleteItem = useCallback(async (
    collectionName: string,
    itemId: string
  ): Promise<boolean> => {
    if (!appId) return false;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${collectionName}/items/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        await fetchItems(collectionName);
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete item');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to delete item');
      return false;
    }
  }, [appId, fetchItems]);

  const importItems = useCallback(async (
    collectionName: string,
    data: Record<string, unknown>[],
    format: 'json' | 'csv' = 'json'
  ): Promise<{ imported: number } | null> => {
    if (!appId) return null;

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${collectionName}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: data, format }),
      });

      const result = await response.json();
      if (result.success) {
        await fetchItems(collectionName);
        return { imported: result.data.imported };
      } else {
        throw new Error(result.error || 'Failed to import items');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to import items');
      return null;
    }
  }, [appId, fetchItems]);

  // ─────────────────────────────────────────────────────────
  // AI Generation
  // ─────────────────────────────────────────────────────────

  const generateItems = useCallback(async (
    collectionName: string,
    prompt: string,
    count: number = 5
  ): Promise<GenerateResult | null> => {
    if (!appId) return null;

    setIsGenerating(true);
    setItemsError(null);

    try {
      const response = await fetch(`/api/apps/${appId}/collections/${collectionName}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt, count }),
      });

      const result = await response.json();
      if (result.success) {
        await fetchItems(collectionName);
        return {
          generated: result.data.generated,
          items: result.data.items,
        };
      } else {
        throw new Error(result.error || 'Failed to generate items');
      }
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to generate items');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [appId, fetchItems]);

  // ─────────────────────────────────────────────────────────
  // AI Schema Assistant
  // ─────────────────────────────────────────────────────────

  const generateSchema = useCallback(async (
    description: string,
    examples?: string
  ): Promise<GenerateSchemaResult | null> => {
    if (!appId) return null;

    setIsGeneratingSchema(true);
    setSchemasError(null);

    try {
      const response = await fetch(`/api/apps/${appId}/collections/generate-schema`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description, examples }),
      });

      const result = await response.json();
      if (result.success) {
        return {
          schema: result.data.schema,
          message: result.data.message,
        };
      } else {
        throw new Error(result.error || 'Failed to generate schema');
      }
    } catch (err) {
      setSchemasError(err instanceof Error ? err.message : 'Failed to generate schema');
      return null;
    } finally {
      setIsGeneratingSchema(false);
    }
  }, [appId]);

  return {
    // Schemas
    schemas,
    isLoadingSchemas,
    schemasError,
    fetchSchemas,
    createSchema,
    updateSchema,
    deleteSchema,
    // Items
    items,
    itemsCount,
    isLoadingItems,
    itemsError,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    importItems,
    // AI generation
    isGenerating,
    generateItems,
    // AI schema assistant
    isGeneratingSchema,
    generateSchema,
  };
}
